import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import googleReviewsService from '../../../services/googleReviewsService';

const FeaturedReviews = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    fetchFeaturedReviews();
  }, []);

  useEffect(() => {
    if (reviews.length > 0) {
      const interval = setInterval(() => {
        setCurrentIndex((prev) => (prev + 1) % reviews.length);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [reviews.length]);

  const fetchFeaturedReviews = async () => {
    try {
      setLoading(true);
      // Get top 5 reviews with text
      const allReviews = await googleReviewsService.getAllReviews();
      const featuredReviews = allReviews
        .filter(r => r.review_text && r.rating === 5)
        .slice(0, 5);
      setReviews(featuredReviews);
    } catch (error) {
      console.error('Error fetching featured reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
    return `${Math.floor(diffDays / 365)} years ago`;
  };

  if (loading || reviews.length === 0) {
    return null;
  }

  const currentReview = reviews[currentIndex];

  return (
    <section className="py-16 bg-gradient-to-b from-muted/20 to-background">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 mb-4"
          >
            <img
              src="https://www.google.com/images/branding/googlelogo/2x/googlelogo_color_92x30dp.png"
              alt="Google"
              className="h-6"
            />
            <span className="text-lg font-semibold text-foreground">Reviews</span>
          </motion.div>
          <h2 className="font-headline text-3xl lg:text-4xl font-bold text-foreground mb-4">
            What Our Patients Say
          </h2>
          <div className="flex items-center justify-center gap-2">
            {[...Array(5)].map((_, i) => (
              <Icon key={i} name="Star" size={20} className="text-yellow-500 fill-yellow-500" />
            ))}
            <span className="ml-2 text-lg font-semibold text-foreground">5.0</span>
            <span className="text-muted-foreground">• {reviews.length} reviews</span>
          </div>
        </div>

        {/* Featured Review Carousel */}
        <div className="max-w-4xl mx-auto">
          <motion.div
            key={currentIndex}
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            transition={{ duration: 0.5 }}
            className="glass-card p-8 lg:p-12 rounded-2xl"
          >
            {/* Quote Icon */}
            <div className="mb-6">
              <Icon name="Quote" size={48} className="text-primary/30" />
            </div>

            {/* Review Text */}
            <p className="text-lg lg:text-xl text-foreground leading-relaxed mb-8 italic">
              "{currentReview.review_text}"
            </p>

            {/* Author Info */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-primary/20 rounded-full flex items-center justify-center">
                  <Icon name="User" size={24} className="text-primary" />
                </div>
                <div>
                  <h4 className="font-semibold text-foreground">{currentReview.author_name}</h4>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Icon name="CheckCircle2" size={14} className="text-green-500" />
                    <span>Verified Patient</span>
                    <span>•</span>
                    <span>{formatDate(currentReview.review_time)}</span>
                  </div>
                </div>
              </div>

              {/* Category Badge */}
              {currentReview.treatment_category && (
                <div className="hidden lg:block">
                  <span className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 text-primary text-sm font-medium rounded-full">
                    <Icon name="Stethoscope" size={14} />
                    {currentReview.treatment_category.name}
                  </span>
                </div>
              )}
            </div>
          </motion.div>

          {/* Navigation Dots */}
          <div className="flex items-center justify-center gap-2 mt-8">
            {reviews.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`w-2 h-2 rounded-full transition-all ${
                  index === currentIndex
                    ? 'bg-primary w-8'
                    : 'bg-muted-foreground/30 hover:bg-muted-foreground/50'
                }`}
                aria-label={`Go to review ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeaturedReviews;

